import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Table, Form, InputGroup, Modal, Alert, Tabs, Tab, Tooltip, OverlayTrigger } from 'react-bootstrap';
import { 
  Plus, 
  Search, 
  Edit, 
  Users,
  Phone,
  Mail,
  Shield,
  Eye,
  RefreshCw,
  UserCheck,
  Key,
  Award,
  Target,
  TrendingUp,
  Save,
  X,
  Package
} from 'lucide-react';
import { userService } from '../../services/userService';
import { saleService } from '../../services/saleService';
import LoadingSpinner from '../common/LoadingSpinner';

const TeamManagement = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('list');
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  
  // Performance data
  const [performanceData, setPerformanceData] = useState([]);
  const [teamStats, setTeamStats] = useState({
    totalEmployees: 0,
    activeEmployees: 0,
    totalSalesToday: 0,
    totalRevenueToday: 0
  });
  
  // Form data
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role: 'EMPLOYEE',
    productAccessType: 'ALL' // ✅ AJOUT

  });
  
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadEmployees();
    loadPerformanceData();
  }, []);

  const loadEmployees = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('👥 Chargement des employés...');
      const response = await userService.getEmployees();
      
      if (response.success) {
        const employeesData = response.data || [];
        setEmployees(employeesData);
        
        const stats = {
  totalEmployees: employeesData.length,
  activeEmployees: employeesData.filter(emp => 
    emp.is_active === true || 
    emp.isActive === true || 
    emp.status === 'ACTIVE' || 
    emp.active === true
  ).length,
  totalSalesToday: 0,
  totalRevenueToday: 0
};

        setTeamStats(stats);
        
        console.log('✅ Employés chargés:', employeesData.length);
      } else {
        setError(response.message || 'Erreur lors du chargement des employés');
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des employés:', error);
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const loadPerformanceData = async () => {
  try {
    console.log('📊 Chargement des performances...');
    const salesResponse = await saleService.getSales();
    
    if (salesResponse.success) {
      const sales = salesResponse.data || [];
      console.log('📊 Ventes récupérées pour performances:', sales.length);
      
      const today = new Date().toISOString().split('T')[0];
      console.log('📅 Date du jour:', today);
      
      const performance = {};
      let totalSalesToday = 0;
      let totalRevenueToday = 0;
      
      sales.forEach((sale, index) => {
        // ✅ CORRECTION : Améliorer le parsing de la date
        let saleDate;
        try {
          const rawDate = sale.saleDate || sale.createdAt || sale.created_at || sale.date;
          saleDate = new Date(rawDate).toISOString().split('T')[0];
        } catch (e) {
          console.warn('⚠️ Date invalide pour la vente:', sale.id, sale.saleDate);
          return; // Ignorer cette vente
        }
        
        // ✅ CORRECTION : Améliorer l'identification du vendeur
        const sellerId = sale.seller?.id || 
                         sale.sellerId || 
                         sale.seller_id || 
                         sale.userId || 
                         sale.user_id ||
                         'unknown';
        
        // ✅ CORRECTION : Améliorer la récupération du nom du vendeur
        let sellerName = 'Employé inconnu';
        if (sale.seller) {
          const firstName = sale.seller.firstName || 
                           sale.seller.first_name || 
                           sale.seller.prenom || 
                           sale.seller.name || '';
          const lastName = sale.seller.lastName || 
                          sale.seller.last_name || 
                          sale.seller.nom || '';
          sellerName = `${firstName} ${lastName}`.trim();
        }
        
        if (!sellerName || sellerName === ' ') {
          sellerName = `Employé ${sellerId}`;
        }
        
        console.log(`📊 Vente ${index + 1}:`, {
          saleId: sale.id,
          saleDate,
          sellerId,
          sellerName,
          total: sale.total,
          isToday: saleDate === today
        });
        
        if (!performance[sellerId]) {
          performance[sellerId] = {
            id: sellerId,
            name: sellerName,
            totalSales: 0,
            totalRevenue: 0,
            salesToday: 0,
            revenueToday: 0,
            averageSale: 0
          };
        }
        
        performance[sellerId].totalSales += 1;
        performance[sellerId].totalRevenue += sale.total || 0;
        
        if (saleDate === today) {
          performance[sellerId].salesToday += 1;
          performance[sellerId].revenueToday += sale.total || 0;
          totalSalesToday += 1;
          totalRevenueToday += sale.total || 0;
          
          console.log(`✅ Vente du jour ajoutée pour ${sellerName}:`, {
            salesToday: performance[sellerId].salesToday,
            revenueToday: performance[sellerId].revenueToday
          });
        }
      });
      
      // Calculer la moyenne des ventes
      Object.values(performance).forEach(perf => {
        perf.averageSale = perf.totalSales > 0 ? perf.totalRevenue / perf.totalSales : 0;
      });
      
      const performanceArray = Object.values(performance).sort((a, b) => b.revenueToday - a.revenueToday);
      
      console.log('📊 Performances calculées:', performanceArray);
      console.log('📊 Total ventes aujourd\'hui:', totalSalesToday);
      console.log('📊 Total CA aujourd\'hui:', totalRevenueToday);
      
      setPerformanceData(performanceArray);
      
      // ✅ CORRECTION : Mettre à jour les stats avec les vraies valeurs
      setTeamStats(prev => ({
        ...prev,
        totalSalesToday,
        totalRevenueToday
      }));
    } else {
      console.warn('⚠️ Échec de récupération des ventes:', salesResponse.message);
    }
  } catch (error) {
    console.error('❌ Erreur lors du chargement des performances:', error);
  }
};

  const filteredEmployees = employees.filter(employee => {
    const searchTerm = searchQuery.toLowerCase();
    return (
      employee.firstName?.toLowerCase().includes(searchTerm) ||
      employee.lastName?.toLowerCase().includes(searchTerm) ||
      employee.email?.toLowerCase().includes(searchTerm) ||
      employee.username?.toLowerCase().includes(searchTerm) ||
      employee.phone?.includes(searchTerm)
    );
  });

  const resetForm = () => {
    console.log('🔄 Reset du formulaire');
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      username: '',
      phone: '',
      password: '',
      confirmPassword: '',
      role: 'EMPLOYEE',
      productAccessType: 'ALL' // ✅ Reset avec valeur par défaut

    });
    setFormErrors({});
  };

  // ✅ NOUVEAU : Fonction pour obtenir le badge productAccessType
  const getProductAccessBadge = (productAccessType) => {
    const accessConfig = {
      'ALL': { 
        bg: 'success', 
        text: '📦🔧 Complet', 
        icon: Package,
        tooltip: 'Accès aux produits stockables et services'
      },
      'STOCKABLE': { 
        bg: 'primary', 
        text: '📦 Stockables', 
        icon: Package,
        tooltip: 'Uniquement produits stockables'
      },
      'SERVICE': { 
        bg: 'info', 
        text: '🔧 Services', 
        icon: Package,
        tooltip: 'Uniquement services'
      }
    };
    
    const config = accessConfig[productAccessType] || accessConfig['ALL'];
    const IconComponent = config.icon;
    
    return (
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip>{config.tooltip}</Tooltip>}
      >
        <Badge bg={config.bg} className="d-flex align-items-center gap-1" style={{ fontSize: '0.75rem' }}>
          <IconComponent size={12} />
          {config.text}
        </Badge>
      </OverlayTrigger>
    );
  };

  const handleInputChange = (field, value) => {
    console.log('📝 Input change:', field, '=', value);
    
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      console.log('📝 Nouveau formData:', newData);
      return newData;
    });
    
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.firstName?.trim()) {
      errors.firstName = 'Le prénom est requis';
    }
    
    if (!formData.lastName?.trim()) {
      errors.lastName = 'Le nom est requis';
    }
    
    if (!formData.email?.trim()) {
      errors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Format d\'email invalide';
    }
    
    if (!formData.username?.trim()) {
      errors.username = 'Le nom d\'utilisateur est requis';
    }
    
    if (!formData.phone?.trim()) {
      errors.phone = 'Le téléphone est requis';
    }
    
    if (modalType === 'create') {
      if (!formData.password) {
        errors.password = 'Le mot de passe est requis';
      } else if (formData.password.length < 6) {
        errors.password = 'Le mot de passe doit contenir au moins 6 caractères';
      }
      
      if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = 'Les mots de passe ne correspondent pas';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      setSubmitting(true);
      
      const userData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        username: formData.username.trim(),
        phone: formData.phone.trim(),
        role: formData.role,
        productAccessType: formData.productAccessType // ✅ Inclure dans les données  
      };
      
      if (modalType === 'create') {
        userData.password = formData.password;
      }
      
      const response = modalType === 'create'
        ? await userService.createEmployee(userData)
        : await userService.updateEmployee(selectedEmployee.id, userData);
      
      if (response.success) {
        await loadEmployees();
        resetForm();
        setShowModal(false);
        setSelectedEmployee(null);
      } else {
        setFormErrors({ submit: response.message });
      }
    } catch (error) {
      console.error('❌ Erreur lors de la sauvegarde:', error);
      setFormErrors({ submit: 'Erreur lors de la sauvegarde' });
    } finally {
      setSubmitting(false);
    }
  };

  const openCreateModal = () => {
    resetForm();
    setModalType('create');
    setSelectedEmployee(null);
    setShowModal(true);
  };

  const openEditModal = (employee) => {
    setSelectedEmployee(employee);
    setFormData({
      firstName: employee.firstName || employee.first_name || '',
      lastName: employee.lastName || employee.last_name || '',
      email: employee.email || '',
      username: employee.username || '',
      phone: employee.phone || '',
      password: '',
      confirmPassword: '',
      role: employee.role || 'EMPLOYEE',
      productAccessType: employee.productAccessType || 'ALL' // ✅ Charger la valeur

    });
    setFormErrors({});
    setModalType('edit');
    setShowModal(true);
  };

  const openDetailsModal = (employee) => {
    setSelectedEmployee(employee);
    setShowDetailsModal(true);
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const getEmployeePerformance = (employeeId) => {
    return performanceData.find(perf => perf.id === employeeId) || {
      totalSales: 0,
      totalRevenue: 0,
      salesToday: 0,
      revenueToday: 0,
      averageSale: 0
    };
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      'SUPER_ADMIN': { bg: 'danger', text: 'Super Admin', icon: Shield },
      'ADMIN': { bg: 'warning', text: 'Admin', icon: Key },
      'MANAGER': { bg: 'primary', text: 'Manager', icon: UserCheck },
      'EMPLOYEE': { bg: 'success', text: 'Employé', icon: Users }
    };
    
    const config = roleConfig[role] || roleConfig['EMPLOYEE'];
    const IconComponent = config.icon;
    
    return (
      <Badge bg={config.bg} className="d-flex align-items-center gap-1">
        <IconComponent size={12} />
        {config.text}
      </Badge>
    );
  };

  if (loading) {
    return <LoadingSpinner message="Chargement de l'équipe..." />;
  }

  return (
    <Container fluid className="py-4">
      {/* En-tête */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1 className="h3 mb-1">
                <Users className="me-2" size={28} />
                Gestion de l'Équipe
              </h1>
              <p className="text-muted mb-0">
                Gérez votre équipe et suivez les performances de vos employés
              </p>
            </div>
            <div className="d-flex gap-2">
              <Button variant="outline-primary" onClick={() => {
                loadEmployees();
                loadPerformanceData();
              }}>
                <RefreshCw size={16} className="me-1" />
                Actualiser
              </Button>
              <Button variant="primary" onClick={openCreateModal}>
                <Plus size={16} className="me-1" />
                Nouvel Employé
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* Statistiques d'équipe */}
      <Row className="mb-4">
        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="bg-primary rounded p-3 me-3">
                  <Users size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="mb-1">{teamStats.totalEmployees}</h3>
                  <p className="text-muted mb-0 small">Total employés</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="bg-success rounded p-3 me-3">
                  <UserCheck size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="mb-1">{teamStats.activeEmployees}</h3>
                  <p className="text-muted mb-0 small">Employés actifs</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="bg-warning rounded p-3 me-3">
                  <Target size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="mb-1">{teamStats.totalSalesToday}</h3>
                  <p className="text-muted mb-0 small">Ventes aujourd'hui</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="bg-info rounded p-3 me-3">
                  <TrendingUp size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="mb-1">{formatAmount(teamStats.totalRevenueToday)}</h3>
                  <p className="text-muted mb-0 small">CA aujourd'hui</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {/* Barre de recherche */}
      <Row className="mb-4">
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <InputGroup>
                <InputGroup.Text>
                  <Search size={16} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Rechercher un employé par nom, email ou téléphone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </InputGroup>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Onglets */}
      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
        <Tab eventKey="list" title={
          <span>
            <Users size={16} className="me-1" />
            Liste des employés
          </span>
        }>
          {/* Vue Desktop - Tableau */}
          <div className="d-none d-md-block">
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white border-0 py-3">
                <h5 className="mb-0">
                  👥 Équipe ({filteredEmployees.length})
                </h5>
              </Card.Header>
              <Card.Body className="p-0">
                {filteredEmployees.length === 0 ? (
                  <div className="text-center py-5">
                    <Users size={48} className="text-muted mb-3" />
                    <h5 className="text-muted">
                      {searchQuery ? 'Aucun employé trouvé' : 'Aucun employé dans votre équipe'}
                    </h5>
                    <p className="text-muted mb-3">
                      {searchQuery 
                        ? 'Essayez avec d\'autres mots-clés'
                        : 'Commencez par ajouter votre premier employé'
                      }
                    </p>
                    {!searchQuery && (
                      <Button variant="primary" onClick={openCreateModal}>
                        <Plus size={16} className="me-1" />
                        Ajouter un employé
                      </Button>
                    )}
                  </div>
                ) : (
                  <Table responsive hover className="mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th>Employé</th>
                        <th>Contact</th>
                        <th>Rôle</th>
                        {/* ✅ NOUVELLE COLONNE */}
                  <th>Accès Produits</th>
                        <th>Statut</th>
                        <th>Performance</th>
                        <th>Membre depuis</th>
                        <th width="120">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredEmployees.map((employee) => {
                        const performance = getEmployeePerformance(employee.id);
                        
                        return (
                          <tr key={employee.id}>
                            <td>
                              <div className="d-flex align-items-center">
                                <div 
                                  className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                                  style={{ width: '40px', height: '40px', fontSize: '14px', fontWeight: 'bold' }}
                                >
                                  {`${(employee.firstName || employee.first_name || employee.prenom || '')?.[0] || ''}${(employee.lastName || employee.last_name || employee.nom || '')?.[0] || ''}`.toUpperCase()}
                                </div>
                                <div>
                                  <div className="fw-medium">
                                    {`${employee.firstName || employee.first_name || employee.prenom || ''} ${employee.lastName || employee.last_name || employee.nom || ''}`.trim()}
                                  </div>
                                  <small className="text-muted">@{employee.username}</small>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div>
                                <div className="d-flex align-items-center gap-1 mb-1">
                                  <Mail size={12} className="text-muted" />
                                  <small>{employee.email}</small>
                                </div>
                                <div className="d-flex align-items-center gap-1">
                                  <Phone size={12} className="text-muted" />
                                  <small>{employee.phone}</small>
                                </div>
                              </div>
                            </td>
                            <td>
                              {getRoleBadge(employee.role)}
                            </td>
                            {/* ✅ NOUVELLE CELLULE pour productAccessType */}
                      <td>
                        {getProductAccessBadge(employee.productAccessType || 'ALL')}
                      </td>
                            <td>
                              <Badge bg={
                                employee.is_active === true || 
                                employee.isActive === true || 
                                employee.status === 'ACTIVE' || 
                                employee.active === true ? "success" : "secondary"
                              }>
                                {employee.is_active === true || 
                                 employee.isActive === true || 
                                 employee.status === 'ACTIVE' || 
                                 employee.active === true ? "Actif" : "Inactif"}
                              </Badge>
                            </td>
                            <td>
                              <div>
                                <div className="fw-medium text-success">
                                  {formatAmount(performance.revenueToday)}
                                </div>
                                <small className="text-muted">
                                  {performance.salesToday} vente(s) aujourd'hui
                                </small>
                              </div>
                            </td>
                            <td>
                              <small className="text-muted">
                                {(employee.createdAt || employee.created_at)
                                  ? new Date(employee.createdAt || employee.created_at).toLocaleDateString('fr-FR')
                                  : '-'
                                }
                              </small>
                            </td>
                            <td>
                              <div className="d-flex gap-1">
                                <Button
                                  variant="outline-info"
                                  size="sm"
                                  onClick={() => openDetailsModal(employee)}
                                  title="Voir détails"
                                >
                                  <Eye size={14} />
                                </Button>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => openEditModal(employee)}
                                  title="Modifier"
                                >
                                  <Edit size={14} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </div>

          {/* Vue Mobile - Cards */}
          <div className="d-md-none">
            {filteredEmployees.length === 0 ? (
              <Card className="border-0 shadow-sm">
                <Card.Body className="text-center py-5">
                  <Users size={48} className="text-muted mb-3" />
                  <h5 className="text-muted">
                    {searchQuery ? 'Aucun employé trouvé' : 'Aucun employé dans votre équipe'}
                  </h5>
                  <p className="text-muted mb-3">
                    {searchQuery 
                      ? 'Essayez avec d\'autres mots-clés'
                      : 'Commencez par ajouter votre premier employé'
                    }
                  </p>
                  {!searchQuery && (
                    <Button variant="primary" onClick={openCreateModal}>
                      <Plus size={16} className="me-1" />
                      Ajouter un employé
                    </Button>
                  )}
                </Card.Body>
              </Card>
            ) : (
              <Row>
                {filteredEmployees.map((employee) => {
                  const performance = getEmployeePerformance(employee.id);
                  
                  return (
                    <Col key={employee.id} xs={12} className="mb-3">
                      <Card className="border-0 shadow-sm">
                        <Card.Body>
                          {/* Header avec avatar et nom */}
                          <div className="d-flex align-items-center mb-3">
                            <div 
                              className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                              style={{ width: '50px', height: '50px', fontSize: '18px', fontWeight: 'bold' }}
                            >
                              {`${(employee.firstName || employee.first_name || '')?.[0] || ''}${(employee.lastName || employee.last_name || '')?.[0] || ''}`.toUpperCase()}
                            </div>
                            <div className="flex-grow-1">
                              <h6 className="fw-bold mb-1">
                                {`${employee.firstName || employee.first_name || ''} ${employee.lastName || employee.last_name || ''}`.trim()}
                              </h6>
                              <small className="text-muted">@{employee.username}</small>
                            </div>
                            <div className="d-flex gap-1">
                              <Button
                                variant="outline-info"
                                size="sm"
                                onClick={() => openDetailsModal(employee)}
                              >
                                <Eye size={14} />
                              </Button>
                              <Button
                                variant="outline-warning"
                                size="sm"
                                onClick={() => openEditModal(employee)}
                              >
                                <Edit size={14} />
                              </Button>
                            </div>
                          </div>

                          {/* Rôle et statut */}
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            {getRoleBadge(employee.role)}
                            <Badge bg={
                              employee.is_active === true || 
                              employee.isActive === true || 
                              employee.status === 'ACTIVE' || 
                              employee.active === true ? "success" : "secondary"
                            }>
                              {employee.is_active === true || 
                               employee.isActive === true || 
                               employee.status === 'ACTIVE' || 
                               employee.active === true ? "Actif" : "Inactif"}
                            </Badge>
                          </div>

                          {/* Contact */}
                          <div className="mb-3">
                            <div className="d-flex align-items-center gap-2 mb-2">
                              <Mail size={16} className="text-muted" />
                              <small>{employee.email}</small>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <Phone size={16} className="text-muted" />
                              <small>{employee.phone}</small>
                            </div>
                          </div>

                          {/* Performance en grid */}
                          <div className="bg-light rounded p-3">
                            <div className="row g-3 text-center">
                              <div className="col-6">
                                <div className="fw-bold text-success h6 mb-1">
                                  {formatAmount(performance.revenueToday)}
                                </div>
                                <small className="text-muted">CA aujourd'hui</small>
                              </div>
                              <div className="col-6">
                                <div className="fw-bold text-primary h6 mb-1">
                                  {performance.salesToday}
                                </div>
                                <small className="text-muted">Ventes</small>
                              </div>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
          </div>
        </Tab>

        <Tab eventKey="performance" title={
          <span>
            <Award size={16} className="me-1" />
            Performances
          </span>
        }>
          {/* Vue Desktop - Tableau */}
          <div className="d-none d-md-block">
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white border-0 py-3">
                <h5 className="mb-0">
                  🏆 Classement des performances (Aujourd'hui)
                </h5>
              </Card.Header>
              <Card.Body className="p-0">
                {performanceData.length === 0 ? (
                  <div className="text-center py-5">
                    <Award size={48} className="text-muted mb-3" />
                    <h5 className="text-muted">Aucune vente aujourd'hui</h5>
                    <p className="text-muted">Les performances apparaîtront après les premières ventes</p>
                  </div>
                ) : (
                  <Table responsive className="mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th width="60">Rang</th>
                        <th>Employé</th>
                        <th>Ventes aujourd'hui</th>
                        <th>CA aujourd'hui</th>
                        <th>Panier moyen</th>
                        <th>Total ventes</th>
                        <th>CA total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {performanceData.map((perf, index) => (
                        <tr key={perf.id}>
                          <td>
                            <div className={`badge ${index === 0 ? 'bg-warning' : index === 1 ? 'bg-secondary' : index === 2 ? 'bg-warning-subtle text-warning' : 'bg-light text-dark'}`}>
                              #{index + 1}
                            </div>
                          </td>
                          <td>
                            <div className="fw-medium">{perf.name}</div>
                          </td>
                          <td>
                            <span className="fw-bold text-primary">{perf.salesToday}</span>
                          </td>
                          <td>
                            <span className="fw-bold text-success">{formatAmount(perf.revenueToday)}</span>
                          </td>
                          <td>
                            <span className="text-muted">{formatAmount(perf.averageSale)}</span>
                          </td>
                          <td>
                            <span className="text-muted">{perf.totalSales}</span>
                          </td>
                          <td>
                            <span className="text-muted">{formatAmount(perf.totalRevenue)}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </div>

          {/* Vue Mobile - Cards Performance */}
          <div className="d-md-none">
            {performanceData.length === 0 ? (
              <Card className="border-0 shadow-sm">
                <Card.Body className="text-center py-5">
                  <Award size={48} className="text-muted mb-3" />
                  <h5 className="text-muted">Aucune vente aujourd'hui</h5>
                  <p className="text-muted">Les performances apparaîtront après les premières ventes</p>
                </Card.Body>
              </Card>
            ) : (
              <Row>
                {performanceData.map((perf, index) => (
                  <Col key={perf.id} xs={12} className="mb-3">
                    <Card className="border-0 shadow-sm">
                      <Card.Body>
                        {/* Header avec rang et nom */}
                        <div className="d-flex align-items-center justify-content-between mb-3">
                          <div className="d-flex align-items-center">
                            <div 
                              className={`badge me-3 ${
                                index === 0 ? 'bg-warning' : 
                                index === 1 ? 'bg-secondary' : 
                                index === 2 ? 'bg-warning-subtle text-warning' : 
                                'bg-light text-dark'
                              }`}
                              style={{ fontSize: '16px', padding: '8px 12px' }}
                            >
                              #{index + 1}
                            </div>
                            <div>
                              <h6 className="fw-bold mb-0">{perf.name}</h6>
                              <small className="text-muted">Employé</small>
                            </div>
                          </div>
                          {/* Médaille pour le podium */}
                          {index < 3 && (
                            <div className="text-warning">
                              <Award size={24} />
                            </div>
                          )}
                        </div>

                        {/* Performances aujourd'hui */}
                        <div className="bg-light rounded p-3 mb-3">
                          <h6 className="text-center mb-3 text-primary">📊 Aujourd'hui</h6>
                          <div className="row g-2 text-center">
                            <div className="col-6">
                              <div className="fw-bold text-primary h5 mb-1">
                                {perf.salesToday}
                              </div>
                              <small className="text-muted">Ventes</small>
                            </div>
                            <div className="col-6">
                              <div className="fw-bold text-success h5 mb-1">
                                {formatAmount(perf.revenueToday)}
                              </div>
                              <small className="text-muted">Chiffre d'affaires</small>
                            </div>
                          </div>
                        </div>

                        {/* Statistiques globales */}
                        <div className="bg-white border rounded p-3">
                          <h6 className="text-center mb-3 text-muted">📈 Statistiques globales</h6>
                          <div className="row g-2 text-center">
                            <div className="col-4">
                              <div className="fw-bold text-muted">
                                {perf.totalSales}
                              </div>
                              <small className="text-muted">Total ventes</small>
                            </div>
                            <div className="col-4">
                              <div className="fw-bold text-muted">
                                {formatAmount(perf.totalRevenue)}
                              </div>
                              <small className="text-muted">CA total</small>
                            </div>
                            <div className="col-4">
                              <div className="fw-bold text-muted">
                                {formatAmount(perf.averageSale)}
                              </div>
                              <small className="text-muted">Panier moyen</small>
                            </div>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </div>
        </Tab>
      </Tabs>

      {/* Modal Création/Édition */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {modalType === 'create' ? 'Nouvel Employé' : 'Modifier Employé'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {formErrors.submit && (
              <Alert variant="danger">{formErrors.submit}</Alert>
            )}
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Prénom *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    isInvalid={!!formErrors.firstName}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.firstName}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nom *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    isInvalid={!!formErrors.lastName}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.lastName}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            {/* ✅ NOUVEAU CHAMP : Accès aux types de produits */}
            <Form.Group className="mb-3">
              <Form.Label>
                <Package size={16} className="me-2" />
                Accès aux types de produits *
              </Form.Label>
              <Form.Select
                value={formData.productAccessType}
                onChange={(e) => handleInputChange('productAccessType', e.target.value)}
                required
              >
                <option value="ALL">📦🔧 Tous les produits (Stockables + Services)</option>
                <option value="STOCKABLE">📦 Produits stockables uniquement</option>
                <option value="SERVICE">🔧 Services uniquement</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Définit les types de produits que cet employé pourra gérer et vendre
              </Form.Text>
            </Form.Group>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email *</Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    isInvalid={!!formErrors.email}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.email}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nom d'utilisateur *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    isInvalid={!!formErrors.username}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.username}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Téléphone *</Form.Label>
                  <Form.Control
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    isInvalid={!!formErrors.phone}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.phone}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Rôle *</Form.Label>
                  <Form.Select
                  disabled={true}
                    value={formData.role}
                    onChange={(e) => handleInputChange('role', e.target.value)}
                  >
                    <option value="EMPLOYEE">Employé</option>
                    {/* <option value="MANAGER">Manager</option>
                    <option value="ADMIN">Admin</option> */}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            {modalType === 'create' && (
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Mot de passe *</Form.Label>
                    <Form.Control
                      type="password"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      isInvalid={!!formErrors.password}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formErrors.password}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Confirmer mot de passe *</Form.Label>
                    <Form.Control
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      isInvalid={!!formErrors.confirmPassword}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formErrors.confirmPassword}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              <X size={16} className="me-1" />
              Annuler
            </Button>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save size={16} className="me-1" />
                  {modalType === 'create' ? 'Créer' : 'Modifier'}
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal Détails */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Détails de l'employé
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEmployee && (
            <Row>
              <Col md={4} className="text-center mb-4">
                <div 
                  className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3"
                  style={{ width: '80px', height: '80px', fontSize: '32px', fontWeight: 'bold' }}
                >
                  {`${(selectedEmployee.firstName || selectedEmployee.first_name || '')?.[0] || ''}${(selectedEmployee.lastName || selectedEmployee.last_name || '')?.[0] || ''}`.toUpperCase()}
                </div>
                <h5>{`${selectedEmployee.firstName || selectedEmployee.first_name || ''} ${selectedEmployee.lastName || selectedEmployee.last_name || ''}`.trim()}</h5>
                <div className="mb-2">{getRoleBadge(selectedEmployee.role)}</div>
                <Badge bg={
                  selectedEmployee.is_active === true || 
                  selectedEmployee.isActive === true || 
                  selectedEmployee.status === 'ACTIVE' || 
                  selectedEmployee.active === true ? "success" : "secondary"
                }>
                  {selectedEmployee.is_active === true || 
                   selectedEmployee.isActive === true || 
                   selectedEmployee.status === 'ACTIVE' || 
                   selectedEmployee.active === true ? "Actif" : "Inactif"}
                </Badge>
              </Col>
              <Col md={8}>
                <Card className="border-0 bg-light">
                  <Card.Body>
                    <h6 className="mb-3">Informations de contact</h6>
                    <div className="mb-2">
                      <Mail size={16} className="text-muted me-2" />
                      <span>{selectedEmployee.email}</span>
                    </div>
                    <div className="mb-2">
                      <Phone size={16} className="text-muted me-2" />
                      <span>{selectedEmployee.phone}</span>
                    </div>
                    <div className="mb-3">
                      <Users size={16} className="text-muted me-2" />
                      <span>@{selectedEmployee.username}</span>
                    </div>
                    
                    <h6 className="mb-3">Performances</h6>
                    {(() => {
                      const performance = getEmployeePerformance(selectedEmployee.id);
                      return (
                        <Row>
                          <Col sm={6} className="mb-2">
                            <div className="d-flex align-items-center">
                              <Target size={16} className="text-primary me-2" />
                              <div>
                                <div className="fw-bold">{performance.salesToday}</div>
                                <small className="text-muted">Ventes aujourd'hui</small>
                              </div>
                            </div>
                          </Col>
                          <Col sm={6} className="mb-2">
                            <div className="d-flex align-items-center">
                              <TrendingUp size={16} className="text-success me-2" />
                              <div>
                                <div className="fw-bold">{formatAmount(performance.revenueToday)}</div>
                                <small className="text-muted">CA aujourd'hui</small>
                              </div>
                            </div>
                          </Col>
                          <Col sm={6} className="mb-2">
                            <div className="d-flex align-items-center">
                              <Award size={16} className="text-warning me-2" />
                              <div>
                                <div className="fw-bold">{performance.totalSales}</div>
                                <small className="text-muted">Total ventes</small>
                              </div>
                            </div>
                          </Col>
                          <Col sm={6} className="mb-2">
                            <div className="d-flex align-items-center">
                              <TrendingUp size={16} className="text-info me-2" />
                              <div>
                                <div className="fw-bold">{formatAmount(performance.averageSale)}</div>
                                <small className="text-muted">Vente moyenne</small>
                              </div>
                            </div>
                          </Col>
                        </Row>
                      );
                    })()}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Fermer
          </Button>
          {selectedEmployee && (
            <Button variant="warning" onClick={() => {
              setShowDetailsModal(false);
              openEditModal(selectedEmployee);
            }}>
              <Edit size={16} className="me-1" />
              Modifier
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default TeamManagement;